import api from '../core/api';

export interface SettingKeyDef {
  key: string;
  label: string;
  description: string;
  type: string;
  defaultValue?: string;
  options?: { value: string; label: string }[];
  optionsSource?: 'timezones' | 'currencies' | 'languages';
  isEncrypted?: boolean;
  accept?: string;
  validation?: { min?: number; max?: number; pattern?: string; minLength?: number; maxLength?: number; required?: boolean; integer?: boolean; step?: number };
  unit?: string;
  money?: boolean;
  usedBy?: string;
  upload?: SettingUploadRules;
  format?: 'sender_email' | 'push_subject' | 'vapid_public_key' | 'vapid_private_key' | 'fcm_server_key' | 'invoice_prefix'
    | 's3_bucket' | 's3_access_key_id' | 's3_secret_key' | 's3_public_url'
    | 'google_api_key' | 'socket_url' | 'analytics_id'
    | 'ai_api_key' | 'ai_endpoint' | 'ai_model';
  /** Key only takes effect while another key in the same category has (or does not have) this value. */
  activeWhen?: { key: string; equals?: string; notEquals?: string };
}

export function isGateOpen(gate: NonNullable<SettingKeyDef['activeWhen']>, value: string | undefined): boolean {
  return gate.equals != null ? value === gate.equals : value !== gate.notEquals;
}

export interface SettingUploadRules {
  formats: Array<'png' | 'jpeg' | 'webp' | 'gif' | 'svg' | 'ico'>;
  maxBytes: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  square?: boolean;
  recommended?: string;
}

export interface StoredFileInfo {
  storage: 'local' | 's3' | 'external';
  exists: boolean | null;
  fileName: string;
  bytes?: number;
  width?: number;
  height?: number;
  format?: string;
  problem?: string;
}

export interface SettingCategoryDef {
  key: string;
  label: string;
  description: string;
  icon: string;
  keys: SettingKeyDef[];
}

export interface SettingRow {
  _id: string | null;
  category: string;
  key: string;
  value: string;
  description?: string;
  isEncrypted?: boolean;
  hasValue?: boolean;
  /** Why a saved value is not valid / not applied (null when fine). */
  issue?: string | null;
  /** Unsaved default that conflicts with another setting (e.g. toggle ON without its provider). */
  warning?: string | null;
  /** Valid but weak (security posture) — not counted as configured. */
  advisory?: string | null;
  /** Unused because the switch it depends on is off — not counted as configured. */
  inactive?: string | null;
  updatedAt?: string | null;
  /** Image/file keys: what is actually stored behind the URL. */
  fileInfo?: StoredFileInfo | null;
}

export interface SettingsContext {
  country: { code: string; name: string; dialCode: string; phoneLength: { min: number; max: number } | null };
  timezones: { value: string; label: string }[];
  currencies: Array<{
    value: string;
    label: string;
    symbol: string;
    decimalPlaces: number;
    position: 'before' | 'after';
    thousandSeparator: string;
    decimalSeparator: string;
  }>;
  languages: Array<{ value: string; label: string; locale: string; isDefault: boolean; provisioned: boolean }>;
  tenant: { timezone: string; currency: string; defaultLanguage: string };
  storage: {
    provider: 'local' | 's3';
    bucket?: string;
    region?: string;
    s3Incomplete: boolean;
    enabled?: boolean;
    verification?: { ok: boolean; at: string; message: string; current: boolean } | null;
    localFiles?: number;
  };
  payments?: {
    gateways: Array<{
      type: string;
      label: string;
      env: 'sandbox' | 'live';
      enabled: boolean;
      configured: boolean;
      issues: string[];
      supportsManualCapture: boolean;
    }>;
    wallet?: { featureEnabled: boolean; gatewayEnabled: boolean };
  };
  notifications?: {
    fcm: { mode: 'v1' | 'legacy' | 'off'; serviceAccount: boolean; serverKeyStored: boolean; serverKeyIssue: string | null };
    webPush: {
      source: 'env' | 'settings' | 'none';
      publicKeyStored: boolean;
      privateKeyStored: boolean;
      derivedPublicKey: string | null;
      privateKeyIssue: string | null;
      ready: boolean;
      subject: string;
    };
    templates: Record<string, Array<{ code: string; active: boolean }>>;
    smtpConfigured: boolean;
  };
  security?: {
    codeDelivery: { smtp: boolean; smsLive: boolean; devOtp: boolean };
    emailVerification: { affectedAccounts: number; byType: Record<string, number> };
    admins: { byType: Record<string, number>; withoutContact: number };
    lockedAccounts: number;
  };
  loyalty?: {
    moduleEnabled: boolean;
    activeRules: number;
    referrals: { pending: number; completed: number };
    qualifyingEvent: string;
  };
  ai?: {
    enabled: boolean;
    ocrEnabled: boolean;
    provider: string;
    model: string;
    endpoint: string;
    keyStored: boolean;
    verification: { ok: boolean; visionOk: boolean | null; at: string; message: string; current: boolean } | null;
    ocr: { last30Days: { done: number; failed: number; skipped: number } };
  };
  locale?: {
    profileBase: string;
    currencies: Array<{
      code: string;
      name: string;
      symbol: string;
      decimalPlaces: number;
      roundingRule: string;
      masterRate: number | null;
      masterRateSource: string;
      ecbPublished: boolean;
    }>;
    rates: Array<{ from: string; to: string; rate: number; source: 'manual' | 'ecb'; effectiveFrom: string; history: number }>;
    fxSync: { ok: boolean; at: string; synced: number; rateDate?: string; message: string } | null;
    countryOverrides: number;
  };
  partnerOnboarding?: {
    partnerTypes: Array<{
      entityType: string;
      label: string;
      mandatoryDocuments: string[];
      optionalDocuments: number;
      pendingReview: number;
      rejected: number;
    }>;
  };
  integration?: {
    maps: { stored: boolean; verification: IntegrationVerification | null };
    geocoder: { stored: boolean; source: 'tenant' | 'maps_fallback' | 'platform' | 'none'; verification: IntegrationVerification | null };
    socket: { custom: boolean; url: string; verification: IntegrationVerification | null };
    analytics: { provider: string; id: string; active: boolean };
  };
  subscription?: {
    moduleEnabled: boolean;
    plans: { active: number; withTrial: number };
    subscriptions: { trialing: number; active: number; pastDue: number };
    invoices: { total: number; lastNumber: string | null };
    invoiceSequence: number;
  };
}

export interface SettingsHistoryEntry {
  id: string;
  at: string;
  by: string;
  ip?: string;
  changes: Array<{ category: string; key: string; label: string; from: unknown; to: unknown }>;
}

export interface StorageTestResult {
  ok: boolean;
  steps: Array<{ step: string; ok: boolean; message: string }>;
  bucketRegion?: string;
  sampleUrl?: string;
  at: string;
}

/** Test S3 with the form's current (possibly unsaved) values; masked secrets use the saved ones. */
export interface AiTestResult {
  ok: boolean;
  visionOk: boolean | null;
  unreachable?: boolean;
  steps: Array<{ step: string; ok: boolean; warning?: boolean; message: string }>;
  at: string;
}

/** Draft AI values override the saved ones; a masked key means "use the saved key". */
export async function testAiConnection(values: Record<string, string>) {
  const res = await api.post('/admin/settings/ai/test', { values });
  return res.data.data as AiTestResult;
}

export interface IntegrationVerification {
  ok: boolean;
  at: string;
  message: string;
  restricted?: boolean | null;
  warnings: string[];
  current: boolean;
}

export type IntegrationTestKey = 'maps_api_key' | 'geo_coder_api_key' | 'socket_realtime_url';

export interface IntegrationTestResult {
  key: IntegrationTestKey;
  ok: boolean;
  unreachable?: boolean;
  restricted?: boolean | null;
  steps: Array<{ step: string; ok: boolean; warning?: boolean; message: string }>;
  at: string;
}

/** `value` blank or masked = test the saved value. */
export async function testIntegrationSetting(key: IntegrationTestKey, value?: string) {
  const res = await api.post('/admin/settings/integration/test', { key, value });
  return res.data.data as IntegrationTestResult;
}

export async function testStorageConnection(values: Record<string, string>) {
  const res = await api.post('/admin/settings/storage/test', { values });
  return res.data.data as StorageTestResult;
}

export async function getSettingsHistory(category: string, limit = 5) {
  const res = await api.get('/admin/settings/history', { params: { category, limit } });
  return res.data.data as SettingsHistoryEntry[];
}

export interface PushTestResult {
  ok: boolean;
  pushEnabledReady: boolean;
  checks: Array<{ provider: 'fcm' | 'web_push' | 'delivery'; status: 'ok' | 'error' | 'warning' | 'skipped'; message: string }>;
}

export async function runPushTest() {
  const res = await api.post('/notifications/admin/push-test');
  return res.data.data as PushTestResult;
}

export async function generateVapidKeys() {
  const res = await api.post('/notifications/admin/web-push/generate-keys');
  return res.data.data as { publicKey: string; privateKey: string };
}

export async function getSettingsContext() {
  const res = await api.get('/admin/settings/context');
  return res.data.data as {
    context: SettingsContext;
    options: Record<string, { value: string; label: string }[]>;
    tenant: { id: string; slug: string; name: string };
  };
}

export async function getSettingsCatalog() {
  const res = await api.get('/admin/settings/catalog');
  return res.data.data as SettingCategoryDef[];
}

export async function getSettings(category?: string) {
  const res = await api.get('/admin/settings', { params: category ? { category } : {} });
  return res.data.data as SettingRow[];
}

export async function upsertSetting(payload: { category: string; key: string; value: string; isEncrypted?: boolean }) {
  const res = await api.put('/admin/settings', payload);
  return res.data.data;
}

export async function bulkUpsertSettings(settings: { category: string; key: string; value: string }[]) {
  const res = await api.put('/admin/settings/bulk', { settings });
  return res.data.data;
}

export async function uploadSettingFile(
  file: File,
  category: string,
  key: string,
): Promise<{
  url: string;
  storage: 'local' | 's3';
  fileName: string;
  bytes: number;
  format: string;
  width?: number;
  height?: number;
  replaced: boolean;
}> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  formData.append('key', key);
  const res = await api.post('/admin/settings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data;
}

export async function removeSettingFile(category: string, key: string) {
  const res = await api.post('/admin/settings/upload/remove', { category, key });
  return res.data.data as { removed: string; fileDeleted: boolean };
}
